import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { SmartRedirectGuard } from 'src/app/core/guards/smart-redirect.guard';
import { UserContextResolverService } from 'src/app/core/guards/user-context-resolver.service';
import { CarrierAgencyProfileResolverService } from 'src/app/core/guards/carrier-agency-profile-resolver.service';
import { NetworkResolverService } from 'src/app/core/guards/network-resolver.service';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Network, agency, and carrier profile routes. Grouped here because they share the
 * network/carrier-agency-profile resolvers and use nested custom outlets
 * (network-content, entityContent, agencyProfileContent, carrierProfileContent,
 * carrierAgencyProfileContent) owned by their respective parent components.
 */
export const NETWORK_ROUTES: Routes = [
    {
        path: 'network',
        loadComponent: () => import('src/components/network/network.component').then(m => m.NetworkComponent),
        canActivate: [EnhancedAuthGuard],
        resolve: {
            userContext: UserContextResolverService,
            networkData: NetworkResolverService
        },
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            // Default redirect when navigating to /network with no child route
            {
                path: '',
                pathMatch: 'full',
                canActivate: [SmartRedirectGuard],
                data: {
                    redirectType: 'network'
                } satisfies RouteData
            },
            // Network Agency List
            {
                path: 'agencyList',
                loadComponent: () => import('src/components/agency-listing/agency-listing.component').then(m => m.AgencyListingComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkAgencyList',
                    requiresPermission: true
                } satisfies RouteData
            },

            // Network Hierarchy List
            {
                path: 'hierarchyList',
                loadComponent: () => import('src/components/hierarchy/hierarchy.component').then(m => m.HierarchyComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkHierarchyList',
                    requiresPermission: true
                } satisfies RouteData
            },

            // Network Carrier List
            {
                path: 'carrierList',
                loadComponent: () => import('src/components/carrier-listing/carrier-listing.component').then(m => m.CarrierListingComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkCarrierList',
                    requiresPermission: true
                } satisfies RouteData
            },

            // Network Carrier Agency Redirect
            {
                path: 'carrierAgencyRedirect',
                loadComponent: () => import('src/components/carrier-agency-redirect/carrier-agency-redirect.component').then(m => m.CarrierAgencyRedirectComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkCarrierAgencyRedirect',
                    requiresPermission: true
                } satisfies RouteData
            },

            // Network Resident Licenses Due Soon
            {
                path: 'residentLicensesDueSoon',
                loadComponent: () => import('src/components/resident-licenses-due-soon/resident-licenses-due-soon.component').then(m => m.ResidentLicensesDueSoonComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkResidentLicensesDueSoon',
                    requiresPermission: true
                } satisfies RouteData
            },

            // Network Entities
            {
                path: 'entities',
                loadComponent: () => import('src/components/entities/entities.component').then(m => m.EntitiesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'networkEntities',
                    requiresPermission: true
                } satisfies RouteData
            },

            // NETWORK ENTITY ROUTES
            {
                path: 'entity',
                loadComponent: () => import('src/components/entity-record/entity-record.component').then(m => m.EntityRecordComponent),
                canActivate: [EnhancedAuthGuard],
                resolve: {
                    userContext: UserContextResolverService
                },
                data: {
                    requiresPermission: false
                } satisfies RouteData,
                children: [
                    // Default redirect - handled by EntityRecordComponent logic
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'activity'
                    },
                    // Entity Account
                    {
                        path: 'account',
                        loadComponent: () => import('src/components/entity-record/account/account.component').then(m => m.AccountComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityAccount',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Activities
                    {
                        path: 'activities',
                        loadComponent: () => import('src/components/carrier-producer-activities/carrier-activity-tab-groups/carrier-activity-tab-groups.component').then(m => m.CarrierActivityTabGroupsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityActivities',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Carrier Request
                    {
                        path: 'carrierRequest',
                        loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityCarrierRequest',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Disclosures
                    {
                        path: 'disclosures',
                        loadComponent: () => import('src/components/entity-record/disclosures/disclosures-list.component').then(m => m.DisclosuresListComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityDisclosures',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Employments
                    {
                        path: 'employments',
                        loadComponent: () => import('src/components/entity-record/employments/employments.component').then(m => m.EmploymentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityEmployments',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Activity (single)
                    {
                        path: 'activity',
                        loadComponent: () => import('src/components/entity-record/activity/activity.component').then(m => m.ActivityComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityActivity',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity All Activity
                    {
                        path: 'allActivity',
                        loadComponent: () => import('src/components/all-activity/all-activity-list.component').then(m => m.AllActivityListComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            // Original UI-Router had the plural-typo `requiresPermissions: true`, which the
                            // guard never read - so this route was NOT permission-gated in production.
                            // Keep it ungated to preserve behavior (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                            requiresPermission: false
                        } satisfies RouteData
                    },

                    // Entity Framed Transaction
                    {
                        path: 'framedTransaction',
                        loadComponent: () => import('src/components/framed-transaction/framed-transaction.component').then(m => m.FramedTransactionComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityFramedTransaction',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Licenses V2
                    {
                        path: 'licenses',
                        loadComponent: () => import('src/components/licenses/licenses-v2.component').then(m => m.LicensesV2Component),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityLicenses',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Licenses V1
                    {
                        path: 'licensesv1',
                        loadComponent: () => import('src/components/licenses/licenses.component').then(m => m.LicensesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityLicensesV1',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Personal Summary
                    {
                        path: 'personalSummary',
                        loadComponent: () => import('src/components/personal-summary/personal-summary.component').then(m => m.PersonalSummaryComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityPersonalSummary',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Appointments
                    {
                        path: 'appointments',
                        loadComponent: () => import('src/components/appointments/appointments.component').then(m => m.AppointmentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityAppointments',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Registrations
                    {
                        path: 'registrations',
                        loadComponent: () => import('src/components/entity-record/registrations/registrations.component').then(m => m.RegistrationsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityRegistrations',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Carrier Documents
                    {
                        path: 'carrierDocuments',
                        loadComponent: () => import('src/components/carrier-producer-documents/carrier-producer-documents.component').then(m => m.CarrierProducerDocumentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityCarrierDocuments',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Producer Contracts
                    {
                        path: 'producerContracts',
                        loadComponent: () => import('src/components/producer-contracts/producer-contracts.component').then(m => m.ProducerContractsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityProducerContracts',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity All Actions
                    {
                        path: 'allActions',
                        loadComponent: () => import('src/components/all-actions/all-actions.component').then(m => m.AllActionsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntityAllActions',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Qualifications
                    {
                        path: 'qualifications',
                        loadComponent: () => import('src/components/entity-record/qualifications/qualifications.component').then(m => m.QualificationsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            requiresPermission: false // No permission check in original
                        } satisfies RouteData
                    },

                    // Entity Selling Relationships
                    {
                        path: 'sellingRelationships',
                        loadComponent: () => import('src/components/selling-relationship/selling-relationship-container.component').then(m => m.SellingRelationshipContainerComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkEntitySellingRelationships',
                            requiresPermission: true
                        } satisfies RouteData
                    },

                    // Entity Compensation
                    {
                        path: 'compensation',
                        loadComponent: () => import('src/components/produce-compensation/producer-compensation.component').then(m => m.ProducerCompensationComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            // Original UI-Router had the plural-typo `requiresPermissions: true` (never read
                            // by the guard), so this route was NOT permission-gated in production. Keep it
                            // ungated to preserve behavior (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                            requiresPermission: false
                        } satisfies RouteData
                    },

                    // ENTITY COMPLIANCE DASHBOARD ROUTES
                    {
                        path: 'compliance',
                        loadComponent: () => import('src/components/compliance-dashboard/compliance-dashboard.component').then(m => m.ComplianceDashboardComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            // Original UI-Router had the plural-typo `requiresPermissions: true` (never read
                            // by the guard), so this route was NOT permission-gated in production. Keep it
                            // ungated to preserve behavior (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                            requiresPermission: false,
                            // permissionKey is required even though requiresPermission is false:
                            // CarrierViewOfProducerNavigationService.isComplianceTabActive() uses it
                            // to uniquely identify this route when deciding which tab to highlight.
                            permissionKey: 'networkEntityComplianceDashboard'
                        } satisfies RouteData
                    },

                    // ENTITY CARRIER PROFILE ROUTES
                    {
                        path: 'carrierProfile',
                        loadComponent: () => import('src/components/carrier-profile/carrier-profile.component').then(m => m.CarrierProfileComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            // Original UI-Router had the plural-typo `requiresPermissions: true` (never read
                            // by the guard), so this route was NOT permission-gated in production. Keep it
                            // ungated to preserve behavior (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                            requiresPermission: false
                        } satisfies RouteData,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'activity'
                            },
                            {
                                path: 'activity',
                                loadComponent: () => import('src/components/carrier-profile/activity/carrier-profile-activity.component').then(m => m.CarrierProfileActivityComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    // Plural-typo `requiresPermissions: true` in original UI-Router = never
                                    // gated. Keep ungated (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                                    requiresPermission: false
                                } satisfies RouteData
                            },
                            {
                                path: 'hierarchyDownline',
                                loadComponent: () => import('src/components/producer-hierarchy/agency-view-of-carrier-hierarchy.component').then(m => m.AgencyViewOfCarrierHierarchyComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    // Plural-typo `requiresPermissions: true` in original UI-Router = never
                                    // gated. Keep ungated (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                                    requiresPermission: false
                                } satisfies RouteData
                            },
                            {
                                path: 'producerContracts',
                                loadComponent: () => import('src/components/carrier-profile/producer-contracts/carrier-profile-producer-contracts.component').then(m => m.CarrierProfileProducerContractsComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    // Plural-typo `requiresPermissions: true` in original UI-Router = never
                                    // gated. Keep ungated (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                                    requiresPermission: false
                                } satisfies RouteData
                            },
                            {
                                path: 'producerCompSchedules',
                                loadComponent: () => import('src/components/producer-comp-schedules/producer-comp-schedules.component').then(m => m.ProducerCompSchedulesComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    // Plural-typo `requiresPermissions: true` in original UI-Router = never
                                    // gated. Keep ungated (see QUERY_PARAM_SERIALIZATION_REGRESSIONS.md).
                                    requiresPermission: false
                                } satisfies RouteData
                            }
                        ]
                    }
                ]
            },

            // NETWORK AGENCY PROFILE ROUTES
            {
                path: 'agencyProfile',
                loadComponent: () => import('src/components/agency-profile/agency-profile.component').then(m => m.AgencyProfileComponent),
                canActivate: [EnhancedAuthGuard],
                resolve: {
                    userContext: UserContextResolverService
                },
                data: {
                    permissionKey: 'networkAgencyProfile',
                    requiresPermission: true
                } satisfies RouteData,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'activity'
                    },
                    {
                        path: 'activity',
                        loadComponent: () => import('src/components/agency-profile/activity/agency-profile-activity.component').then(m => m.AgencyProfileActivityComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileActivity',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'activities',
                        loadComponent: () => import('src/components/carrier-producer-activities/carrier-activity-tab-groups/carrier-activity-tab-groups.component').then(m => m.CarrierActivityTabGroupsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileActivities',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'carrierRequest',
                        loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileCarrierRequest',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'carrierDocuments',
                        loadComponent: () => import('src/components/carrier-producer-documents/carrier-producer-documents.component').then(m => m.CarrierProducerDocumentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileCarrierDocuments',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'sellingRelationships',
                        loadComponent: () => import('src/components/selling-relationship/selling-relationship-container.component').then(m => m.SellingRelationshipContainerComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileSellingRelationships',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'compensation',
                        loadComponent: () => import('src/components/produce-compensation/producer-compensation.component').then(m => m.ProducerCompensationComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileCompensation',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'compliance',
                        loadComponent: () => import('src/components/compliance-dashboard/compliance-dashboard.component').then(m => m.ComplianceDashboardComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileComplianceDashboard',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'personalSummary',
                        loadComponent: () => import('src/components/personal-summary/personal-summary.component').then(m => m.PersonalSummaryComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfilePersonalSummary',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'producerContracts',
                        loadComponent: () => import('src/components/producer-contracts/producer-contracts.component').then(m => m.ProducerContractsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileProducerContracts',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'licensesv1',
                        loadComponent: () => import('src/components/licenses/licenses.component').then(m => m.LicensesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileLicensesV1',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'appointments',
                        loadComponent: () => import('src/components/appointments/appointments.component').then(m => m.AppointmentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkAgencyProfileAppointments',
                            requiresPermission: true
                        } satisfies RouteData
                    }
                ]
            },

            // NETWORK CARRIER AGENCY PROFILE ROUTES
            {
                path: 'carrierAgencyProfile',
                loadComponent: () => import('src/components/carrier-agency-profile/carrier-agency-profile.component').then(m => m.CarrierAgencyProfileComponent),
                canActivate: [EnhancedAuthGuard],
                resolve: {
                    userContext: UserContextResolverService,
                    profileData: CarrierAgencyProfileResolverService
                },
                data: {
                    // Parent must NOT be permission-gated. Angular runs guards hierarchically
                    // (parent canActivate before child), unlike UI-Router which only checked
                    // requiresPermission against the DESTINATION state. Every content child below
                    // self-gates (requiresPermission: true + its own guard), and bare
                    // /carrierAgencyProfile redirects to the gated 'overview'. Crucially, the
                    // 'unauthorized' child exists to gracefully handle users who LACK this
                    // permission - gating the parent bounced them to /error (sad cloud) instead,
                    // so a no-access SSO user could never reach the page built for exactly that case.
                    requiresPermission: false
                } satisfies RouteData,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview'
                    },
                    {
                        path: 'carrierRequest',
                        loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileCarrierRequest',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'downlineCarrierRequest',
                        loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileDownlineCarrierRequest',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'connect',
                        loadComponent: () => import('src/components/carrier-agency-profile/connect/carrier-agency-connect.component').then(m => m.CarrierAgencyConnectComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileConnect',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'overview',
                        loadComponent: () => import('src/components/carrier-agency-profile/overview/carrier-agency-profile-overview.component').then(m => m.CarrierAgencyProfileOverviewComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileOverview',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'profile',
                        loadComponent: () => import('src/components/carrier-agency-profile/agency-profile/agency-profile.component').then(m => m.AgencyProfileComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileProfile',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'producerOnboarding',
                        loadComponent: () => import('src/components/carrier-agency-profile/producer-onboarding/carrier-producer-onboarding.component').then(m => m.CarrierProducerOnboardingComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileProducerOnboarding',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'carrierDocuments',
                        loadComponent: () => import('src/components/producer-carrier-documents/producer-carrier-documents.component').then(m => m.ProducerCarrierDocumentsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileCarrierDocuments',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'activity',
                        loadComponent: () => import('src/components/all-activity/activity-tab-groups/activity-tab-groups.component').then(m => m.ActivityTabGroupsComponent),
                        canActivate: [EnhancedAuthGuard],
                        resolve: {
                            // NOTE: Original route had 4 resolvers - components may need updates to use ActivatedRoute
                            // carrierNetworkId, relationshipId, statusFilterOptions, producerType
                        },
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileActivity',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'hierarchies',
                        loadComponent: () => import('src/components/carrier-agency-profile/producer-hierarchy/carrier-agency-producer-hierarchies.component').then(m => m.CarrierAgencyProducerHierarchiesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileHierarchies',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'producerContracts',
                        loadComponent: () => import('src/components/producer-contracts/producer-contracts.component').then(m => m.ProducerContractsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileProducerContracts',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'producerCompSchedules',
                        loadComponent: () => import('src/components/producer-comp-schedules/producer-comp-schedules.component').then(m => m.ProducerCompSchedulesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileProducerCompSchedules',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'hierarchyDownline',
                        loadComponent: () => import('src/components/producer-hierarchy/agency-view-of-carrier-hierarchy.component').then(m => m.AgencyViewOfCarrierHierarchyComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileHierarchyDownline',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'unauthorized',
                        loadComponent: () => import('src/components/carrier-agency-profile/carrier-agency-profile-unauthorized/carrier-agency-profile-unauthorized.component').then(m => m.CarrierAgencyProfileUnauthorizedComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            // This is the graceful "you don't have access" landing page - it MUST be
                            // reachable by users who lack carrierAgencyProfile permission (that's who
                            // carrier-agency-redirect sends here). Gating it behind the very permission
                            // it exists to handle sent the user to /error instead.
                            requiresPermission: false
                        } satisfies RouteData
                    },
                    {
                        path: 'errorAndOmissionPolicies',
                        loadComponent: () => import('src/components/personal-summary/error-and-omissions/error-and-omissions.component').then(m => m.ErrorAndOmissionsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileErrorAndOmissionPolicies',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'bankDetailsAndPreferences',
                        loadComponent: () => import('src/components/personal-summary/bank-details/bank-details.component').then(m => m.BankDetailsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'networkCarrierAgencyProfileBankDetailsAndPreferences',
                            requiresPermission: true
                        } satisfies RouteData
                    }
                ]
            }
        ]
    },

    // This route is separate from network.entity hierarchy
    {
        path: 'compliance/create',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/compliance-dashboard/create-compliance-profile/create-compliance-profile/create-compliance-profile.component').then(m => m.CreateComplianceProfileComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/compliance-dashboard/create-compliance-profile/create-compliance-profile-workflow-side-rail/create-compliance-profile-workflow-side-rail.component').then(m => m.CreateComplianceProfileWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    }
];
